import { Suspense } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
	createFileRoute,
	Link,
	Outlet,
	useChildMatches,
} from '@tanstack/react-router';

import {
	Box,
	Container,
	LinearProgress,
	Paper,
	Tab,
	Tabs,
} from '@mui/material';

import { claimQueries } from '@repo/api/claim/claims';
import { SingleClaim } from '@repo/api/claim/claims.types';
import { HTTPError } from '@repo/api/client';
import { useUser } from '@repo/auth';

import { ClaimHeader } from './_claim/-components/ClaimHeader';
import { ErrorMessage } from './_claim/-components/ErrorMessage';
import { Route as BusinessRoute } from './_claim/business';
import { Route as DataRoute } from './_claim/data';
import { Route as DocumentsRoute } from './_claim/documents';

function needsRefetch(claim: SingleClaim) {
	return claim.claimDocuments.some(
		(d) =>
			d.documentStatus === 'Processing' ||
			d.documentStatus === 'Document Approval Required',
	);
}

export const Route = createFileRoute('/_authenticated/claim/$claimId/_claim')({
	loader: ({ context, params }) => {
		void context.queryClient.ensureQueryData(claimQueries.id(params.claimId));
	},
	component: function ClaimPage() {
		const params = Route.useParams();

		const {
			data: claim,
			error,
			isLoading,
		} = useQuery(
			claimQueries.id(params.claimId, {
				refetchInterval: (claim) => (needsRefetch(claim) ? 5000 : false),
			}),
		);

		const { permissions } = useUser();

		const matchedId = useChildMatches({
			select: (match) => match[0]?.routeId,
		});

		if (error) {
			if (error instanceof HTTPError) {
				switch (error.response.status) {
					case 404:
						return <ErrorMessage message="Claim not found" />;
					case 403:
						return (
							<ErrorMessage message="You do not have permission to view this claim" />
						);
				}
			}
			return <ErrorMessage message="Unexpected error" />;
		}

		if (isLoading) {
			return <LinearProgress />;
		}

		if (!claim || claim.claimStatus == null) {
			return <ErrorMessage message="Claim not found" />;
		}

		return (
			<Container
				maxWidth="lg"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					flexGrow: 1,
					'& .MuiButton-root': {
						whiteSpace: 'nowrap',
					},
					mt: 2,
				}}
			>
				<ClaimHeader claimId={params.claimId} />
				<Paper
					elevation={1}
					sx={{
						flexGrow: 1,
						display: 'flex',
						flexDirection: 'column',
						backgroundColor: 'transparent',
						borderBottomLeftRadius: 6,
						borderBottomRightRadius: 6,
						boxShadow:
							'0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14)',
					}}
				>
					<Tabs
						variant="scrollable"
						value={matchedId}
						sx={{
							minHeight: '32px',
							'& .MuiTabs-flexContainer': {
								gap: 0.5,
							},
							'& .MuiTab-root': {
								backgroundColor: '#dddddd',
								color: '#424242',
								paddingBlock: 0,
								minHeight: '32px',
								fontSize: '1rem',
								fontWeight: '500',
							},
							'& .Mui-selected': {
								color: '#424242',
								backgroundColor: 'white',
							},
						}}
						slotProps={{
							indicator: {
								sx: {
									transform: 'scaleX(0.7)',
									backgroundColor: 'accent.main',
									height: '3px',
								},
							},
						}}
					>
						<Tab
							value={BusinessRoute.id}
							component={Link}
							to={BusinessRoute.to}
							replace
							label="Business Questionnaire"
							disabled={!permissions.includes('claim.questionnaire.view')}
						/>
						<Tab
							value={DocumentsRoute.id}
							component={Link}
							to={DocumentsRoute.to}
							replace
							label="Documents"
							disabled={
								!claim.questionnaireCompleted ||
								!permissions.includes('claim.document.view')
							}
						/>
						<Tab
							value={DataRoute.id}
							component={Link}
							to={DataRoute.to}
							replace
							label="Extracted Data"
							disabled={
								claim.claimDocuments.length === 0 ||
								!permissions.includes('shared.data.view')
							}
						/>
					</Tabs>
					<Box
						sx={{
							p: 2,
							flexGrow: 1,
							display: 'flex',
							flexDirection: 'column',
							backgroundColor: 'white',
							borderBottomLeftRadius: 6,
							borderBottomRightRadius: 6,
						}}
					>
						<Suspense fallback={<LinearProgress />}>
							<Outlet />
						</Suspense>
					</Box>
				</Paper>
			</Container>
		);
	},
});
